import { Alert, Badge, Box, Button, CircularProgress, Link, Stack, Tooltip, Typography } from '@mui/material';
import { Link as RouterLink } from 'react-router-dom';
import React, { useEffect, useState } from 'react';
import { AlertTriangle, Check, UploadCloud } from 'react-feather';
import { useDispatch, useSelector } from 'react-redux';
import { useAuth } from '../../../authentication/useAuth';
import IconButtonWithMenu from '../../../components/IconButtonWithMenu';
import instance from '../../../utils/imsAxios';
import { setPendingUploads, setUploadsInProgress } from './slice';
import { tokens } from '../../../theme';
import devPreview from '../../../utils/devPreview';

const ago = (iso) => {
    if (!iso) return 'not yet';
    const mins = Math.round((Date.now() - new Date(iso)) / 60000);
    if (mins < 1) return 'just now';
    if (mins < 60) return `${mins} min ago`;
    const hours = Math.round(mins / 60);
    return hours < 48 ? `${hours} h ago` : new Date(iso).toLocaleDateString('en-GB');
};

// Sync status: green when every change has been sent, amber with a count while changes wait,
// red when sending is failing. Changes go out by themselves every minute; Send now does it at once.
const UploadButton = ({ ...rest }) => {
    const [open, setOpen] = useState(false);
    const [sync, setSync] = useState(null);
    const dispatch = useDispatch();
    const { companyId, canView } = useAuth();
    const { pendingUploads, uploadsLoading } = useSelector(state => state.pendingUploads);
    const [sendError, setSendError] = useState('');

    // The node's own sync state (last sent, last received, last error), checked every minute
    useEffect(() => {
        const check = () => instance.get('/health').then(({ data }) => setSync(data?.sync ?? null)).catch(() => setSync(null));
        check();
        const timer = setInterval(check, 60 * 1000);
        return () => clearInterval(timer);
    }, []);

    const waiting = devPreview('sync-sent') ? 0 : pendingUploads?.length ?? 0;
    // The health check says only whether sending is failing; the error itself is on the Sync page
    const failing = devPreview('sync-failing') || (!!sync?.failing && (!sync.lastUpload || new Date(sync.lastErrorAt) > new Date(sync.lastUpload)));
    const state = failing ? 'failing' : waiting ? 'waiting' : 'sent';
    const colour = { failing: tokens.danger, waiting: tokens.warning, sent: tokens.success }[state];
    const summary = { failing: 'Changes are not being sent', waiting: `${waiting} change${waiting === 1 ? '' : 's'} waiting to send`, sent: 'All changes sent' }[state];

    const sendNow = async () => {
        setSendError('');
        dispatch(setUploadsInProgress(true));
        try {
            const { data } = await instance.post('/pendingUploads/upload', { companyId });
            dispatch(setPendingUploads(data));
        } catch (error) {
            setSendError(error.response?.data ?? error.message);
        } finally {
            dispatch(setUploadsInProgress(false));
        }
    };

    const icon = uploadsLoading
        ? <CircularProgress size={20} thickness={6} sx={{ color: tokens.navInk }} />
        : (
            <Badge badgeContent={state === 'waiting' ? waiting : null} max={99}
                sx={{ '& .MuiBadge-badge': { bgcolor: tokens.warning, color: '#fff', fontWeight: 700 } }}>
                <Box sx={{ position: 'relative', display: 'flex' }}>
                    <UploadCloud color={tokens.navInk} />
                    <Box sx={{ position: 'absolute', right: -3, bottom: -3, width: 10, height: 10, borderRadius: '50%', bgcolor: colour, border: '2px solid #fff' }} />
                </Box>
            </Badge>
        );

    return (
        <Tooltip title={open ? "" : summary}>
            <span>
                <IconButtonWithMenu open={open} onClick={() => setOpen((v) => !v)} onMenuClose={() => setOpen(false)} Icon={icon} aria-label={`Sync: ${summary}`} {...rest}>
                    <Stack spacing={1.5} sx={{ width: 280 }}>
                        <Stack direction="row" spacing={1} alignItems="center">
                            {state === 'failing' ? <AlertTriangle size={18} color={colour} /> : state === 'sent' ? <Check size={18} color={colour} /> : <UploadCloud size={18} color={colour} />}
                            <Typography fontWeight={700} sx={{ color: colour }}>{summary}</Typography>
                        </Stack>
                        <Box>
                            <Typography variant="body2" color="text.secondary">Last sent: {ago(sync?.lastUpload)}</Typography>
                            <Typography variant="body2" color="text.secondary">Last received: {ago(sync?.lastReceive)}</Typography>
                        </Box>
                        {failing && (
                            <Alert severity="error" sx={{ py: 0 }}>
                                {canView('sync')
                                    ? <>Failing since {ago(sync?.lastErrorAt)}. <Link component={RouterLink} to="/ims/administration/sync" onClick={() => setOpen(false)}>Open the Sync page</Link> for the reason.</>
                                    : 'Ask an administrator to check the Sync page.'}
                            </Alert>
                        )}
                        {sendError && <Alert severity="error" sx={{ py: 0 }}>{String(sendError)}</Alert>}
                        <Typography variant="caption" color="text.secondary">Changes are sent by themselves every minute while the laptop is online.</Typography>
                        {waiting > 0 && <Button variant="contained" size="small" disabled={uploadsLoading} onClick={sendNow}>Send now</Button>}
                    </Stack>
                </IconButtonWithMenu>
            </span>
        </Tooltip>
    );
};

export default UploadButton;
